/**
 * Guards against persisting or rendering inline Base64 image data as media URLs.
 */
export function isBase64OrDataUrl(value: unknown): boolean {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('data:') || normalized.includes(';base64,')) {
    return true;
  }
  // Raw base64 payload (no data: prefix) — common in legacy stored media_url values
  return /^[a-z0-9+/=\s]{100,}$/i.test(normalized);
}

export function toDataUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed.toLowerCase().startsWith('data:')) {
    return trimmed;
  }
  return `data:image/png;base64,${trimmed}`;
}

/** Convert a data URL or raw base64 string into a File for multipart upload. */
export function dataUrlToFile(dataUrl: string, filename = 'image.png'): File | null {
  try {
    const normalized = toDataUrl(dataUrl);
    const [header, base64] = normalized.split(',');
    if (!base64) {
      return null;
    }
    const mimeMatch = header.match(/data:([^;]+)/i);
    const mime = mimeMatch?.[1] || 'image/png';
    const binary = atob(base64.replace(/\s/g, ''));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const extension = mime.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
    const safeName = filename.includes('.') ? filename : `${filename}.${extension}`;
    return new File([bytes], safeName, { type: mime });
  } catch {
    return null;
  }
}

export function isAllowedMediaUrl(value: unknown): boolean {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }
  if (isBase64OrDataUrl(value)) {
    return false;
  }
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return value.trim().startsWith('/');
  }
}

export function resolveDisplayMediaUrl(
  url: string | null | undefined,
  fallback = ''
): string {
  return isAllowedMediaUrl(url) ? url!.trim() : fallback;
}
